import React, {
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type PropsWithChildren,
} from "react";
import { Button } from "@/components/ui/button";
import { dismissToast, getToastSnapshot, subscribeToToastStore, toast } from "./toast";
import { ToastContext } from "./toast-context";

const variantStyles: Record<"success" | "info" | "warning" | "error", React.CSSProperties> = {
  success: {
    backgroundColor: "var(--toast-success-bg)",
    color: "var(--toast-success-text)",
    borderColor: "var(--toast-success-border)",
  },
  error: {
    backgroundColor: "var(--toast-error-bg)",
    color: "var(--toast-error-text)",
    borderColor: "var(--toast-error-border)",
  },
  warning: {
    backgroundColor: "var(--toast-warning-bg)",
    color: "var(--toast-warning-text)",
    borderColor: "var(--toast-warning-border)",
  },
  info: {
    backgroundColor: "var(--toast-info-bg)",
    color: "var(--toast-info-text)",
    borderColor: "var(--toast-info-border)",
  },
};

export function ToastProvider({ children }: PropsWithChildren) {
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const snapshot = useSyncExternalStore(subscribeToToastStore, getToastSnapshot, getToastSnapshot);
  const { toasts } = snapshot;

  useEffect(() => {
    const timers = timersRef.current;
    const activeIds = new Set(toasts.map((toastRecord) => toastRecord.id));

    for (const toastRecord of toasts) {
      if (toastRecord.durationMs <= 0 || timers.has(toastRecord.id)) {
        continue;
      }

      const timer = setTimeout(() => {
        timers.delete(toastRecord.id);
        dismissToast(toastRecord.id);
      }, toastRecord.durationMs);

      timers.set(toastRecord.id, timer);
    }

    for (const [id, timer] of timers) {
      if (activeIds.has(id)) {
        continue;
      }
      clearTimeout(timer);
      timers.delete(id);
    }
  }, [toasts]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const contextValue = useMemo(() => ({ toast }), []);

  // Persistent polite live region for non-error toasts. A fresh <section> inserted
  // WITH its text already present is not reliably announced by screen readers, so
  // success/info/warning went silent. This region exists from mount and only its text
  // content changes, which SRs do announce. Errors keep role="alert" on their section
  // (assertive insertion is announced). toasts are newest-first, so the first non-error
  // is the latest announcement (P-197).
  const politeAnnouncement = useMemo(() => {
    const latest = toasts.find((toastRecord) => toastRecord.variant !== "error");
    return latest ? [latest.title, latest.description].filter(Boolean).join(". ") : "";
  }, [toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div aria-live="polite" className="sr-only" role="status">
        {politeAnnouncement}
      </div>
      <div
        className="pointer-events-none fixed right-4 top-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-3"
        data-testid="toast-stack"
      >
        {toasts.map((toastRecord) => (
          <section
            className="pointer-events-auto rounded-2xl border px-4 py-3 shadow-soft backdrop-blur"
            key={toastRecord.id}
            role={toastRecord.variant === "error" ? "alert" : undefined}
            style={variantStyles[toastRecord.variant]}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold leading-5">{toastRecord.title}</p>
                {toastRecord.description ? (
                  <p className="text-sm leading-5 opacity-80">{toastRecord.description}</p>
                ) : null}
              </div>
              <button
                aria-label="Dismiss notification"
                className="rounded-md px-2 py-1 text-xs font-medium opacity-60 transition hover:opacity-100"
                onClick={() => dismissToast(toastRecord.id)}
                type="button"
              >
                Dismiss
              </button>
            </div>
            {toastRecord.action ? (
              <div className="mt-3">
                <Button
                  className="h-8 rounded-lg px-3 text-xs"
                  onClick={() => {
                    toastRecord.action?.onClick?.();
                    dismissToast(toastRecord.id);
                  }}
                  size="sm"
                  type="button"
                  variant={toastRecord.variant === "error" ? "secondary" : "ghost"}
                >
                  {toastRecord.action.label}
                </Button>
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
